import { Controller } from "@hotwired/stimulus";

/**
 * Sampling Brand Canvas Controller (Restaurant
 * Audit, Step 4).
 *
 * Drives the streaming-reveal of the "Tu marca, en
 * tres palabras" step.
 *
 * Architecture (CAL-264). The sampling_brand canvas
 * is rendered ONCE on page load and never swapped by
 * `audit-stage`. All state is value-driven from
 * Stimulus values that the server updates via
 * `set_dataset_attributes` Turbo Stream actions. Pre-
 * rendered swatch + tone-chip HTML chunks ship as
 * JSON-encoded arrays of strings on the dataset; we
 * paste them into their slots.
 *
 * Lifecycle:
 *
 * 1. **connect()**: render whatever values are
 *    present at connect-time. If the payload was
 *    already on screen at page load (replay /
 *    already-done audit), the swatch / tone HTML
 *    arrays are non-empty: we paste them in
 *    immediately. If empty, the server-rendered
 *    skeletons stay put until narration arrives.
 *
 * 2. **`narratedValueChanged`**: fires when the
 *    payload arrives via dataset broadcast. We
 *    replace the storefront slot with the real
 *    image, fill the source pill, paste the swatch
 *    chips, fill the typography slot, and paste the
 *    tone chips. Then dispatch `audit-step:complete`
 *    so the Continue button enables.
 *
 * No time-based cascades. Animations from the
 * sub-controllers (`audit-reveal`) inside the pasted
 * HTML chunks run naturally on whatever HTML lands.
 */
export default class extends Controller {
  static values = {
    narrated: { type: String, default: "false" },
    storefrontImageUrl: {
      type: String,
      default: "",
    },
    sourcePillText: { type: String, default: "" },
    paletteMeta: { type: String, default: "" },
    swatchesHtml: { type: Array, default: [] },
    fontSample: { type: String, default: "" },
    fontRationale: { type: String, default: "" },
    toneWordsHtml: { type: Array, default: [] },
  };

  static targets = [
    "storefrontSlot",
    "sourcePill",
    "sourcePillText",
    "paletteMeta",
    "swatchesSlot",
    "typographySlot",
    "tonesSlot",
  ];

  connect() {
    this.rendered = false;
    this.maybeRender();
  }

  narratedValueChanged(value, previousValue) {
    // Stimulus fires every value-changed callback
    // once on connect with previousValue=undefined.
    // The connect() flow handles initial state.
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.maybeRender();
  }

  storefrontImageUrlValueChanged(_v, prev) {
    if (prev === undefined) return;
    this.maybeRender();
  }

  swatchesHtmlValueChanged(_v, prev) {
    if (prev === undefined) return;
    this.maybeRender();
  }

  toneWordsHtmlValueChanged(_v, prev) {
    if (prev === undefined) return;
    this.maybeRender();
  }

  // -------- render --------

  maybeRender() {
    if (this.rendered) return;
    if (this.narratedValue !== "true") return;

    this.renderStorefront();
    this.renderSourcePill();
    this.renderPaletteMeta();
    this.renderSwatches();
    this.renderTypography();
    this.renderTones();

    this.rendered = true;
    this.signalComplete();
  }

  renderStorefront() {
    if (!this.hasStorefrontSlotTarget) return;
    const url = this.storefrontImageUrlValue;
    if (!url) return;

    const slot = this.storefrontSlotTarget;
    slot.innerHTML = "";

    const wrapper = document.createElement("div");
    wrapper.className = "absolute inset-0";
    wrapper.dataset.controller = "audit-reveal";
    wrapper.dataset.auditRevealDelayValue = "0";

    const img = document.createElement("img");
    img.src = url;
    img.alt = "";
    img.referrerPolicy = "no-referrer";
    img.className =
      "w-full h-full object-cover";
    img.dataset.testid = "brand-source-image";
    wrapper.appendChild(img);

    slot.appendChild(wrapper);
  }

  renderSourcePill() {
    if (!this.hasSourcePillTarget) return;
    const text = this.sourcePillTextValue;
    if (!text) return;

    if (this.hasSourcePillTextTarget) {
      this.sourcePillTextTarget.textContent = text;
    }
    this.sourcePillTarget.classList.remove("hidden");
  }

  renderPaletteMeta() {
    if (!this.hasPaletteMetaTarget) return;
    this.paletteMetaTarget.textContent =
      this.paletteMetaValue;
  }

  renderSwatches() {
    if (!this.hasSwatchesSlotTarget) return;
    const chunks = this.swatchesHtmlValue;
    if (!chunks.length) return;

    const slot = this.swatchesSlotTarget;
    slot.innerHTML = "";
    // The slot was rendered as a skeleton wrapper:
    // remove the testid so the revealed state isn't
    // misidentified as "still loading".
    delete slot.dataset.testid;

    chunks.forEach((html) => {
      const wrapper = document.createElement("div");
      wrapper.className = "contents";
      wrapper.innerHTML = html;
      const chip = wrapper.firstElementChild;
      if (chip) slot.appendChild(chip);
    });
  }

  renderTypography() {
    if (!this.hasTypographySlotTarget) return;
    const sample = this.fontSampleValue;
    const rationale = this.fontRationaleValue;
    if (!sample && !rationale) return;

    const slot = this.typographySlotTarget;
    slot.innerHTML = "";
    delete slot.dataset.testid;

    const wrapper = document.createElement("div");
    wrapper.className = "flex flex-col gap-1.5";
    wrapper.dataset.controller = "audit-reveal";
    wrapper.dataset.auditRevealDelayValue = "0.9";

    const sampleEl = document.createElement("div");
    sampleEl.className =
      "font-[Fraunces,Georgia,serif] " +
      "text-[42px] leading-[48px] " +
      "tracking-[-0.015em] " +
      "font-semibold text-base-content";
    sampleEl.textContent = sample;
    sampleEl.dataset.testid =
      "brand-type-sample";
    wrapper.appendChild(sampleEl);

    const rationaleEl =
      document.createElement("div");
    rationaleEl.className =
      "text-[13px] leading-4 " +
      "text-base-content/55";
    rationaleEl.textContent = rationale;
    wrapper.appendChild(rationaleEl);

    slot.appendChild(wrapper);
  }

  renderTones() {
    if (!this.hasTonesSlotTarget) return;
    const chunks = this.toneWordsHtmlValue;
    if (!chunks.length) return;

    const slot = this.tonesSlotTarget;
    slot.innerHTML = "";
    delete slot.dataset.testid;

    chunks.forEach((html) => {
      const wrapper = document.createElement("div");
      wrapper.className = "contents";
      wrapper.innerHTML = html;
      const chip = wrapper.firstElementChild;
      if (chip) slot.appendChild(chip);
    });
  }

  // -------- completion --------

  signalComplete() {
    // Stamp the parent stage element with a
    // completion flag BEFORE dispatching the event.
    // Stimulus controller connect order is non-
    // deterministic: if this controller connects
    // and fires the event before audit-continue-
    // button attaches its listener, the event is
    // lost. The flag lets the button's connect() see
    // the completion regardless of attach order.
    const stage = this.element.closest(
      '[data-audit-stage-target="stage"]'
    );
    if (stage) {
      stage.dataset.auditStepComplete = "true";
    }

    document.dispatchEvent(
      new CustomEvent("audit-step:complete", {
        detail: {stepKey: "sampling_brand"},
      })
    );
  }
}
